import './Invite.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import copyToClipboard from 'copy-text-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  Header,
  InlineHeader,
  List,
  ListItem,
  Placeholder,
  ScrollView,
  Tabs,
  TabsItem,
} from '$uikit';
import { useCurrentUserInfo, useTranslator, useUserInfo } from '$hooks';
import {
  Loader,
  MetaTags,
  SocialButtons,
  UnitName,
  UnitPhoto,
  UserCell,
} from '$shared/components';
import { toastActions } from '$store/toast';
import { inviteActions, inviteSelector } from '$store/invite';
import { contentPrioritySelector, mainSelector } from '$store/main';
import { ContentPriorityType } from '$store/main/interface';
import { _prompt, getDocumentHref, rnbr, roundNumber, statReachGoal } from '$utils';
import { IcCopy20, IcSocialLink28 } from '$assets';
import { showModal } from '$navigation/helper';
import { InviteBonus } from '$core/Invite/InviteBonus/InviteBonus';
import { InviteBonusFans } from '$core/Invite/InviteBonusFans/InviteBonusFans';
import { PromoLinks } from '$core/Invite/PromoLinks/PromoLinks';
import { useRoute } from 'react-router5';
import { TELEGRAM_BOT_USERNAME } from '$shared/constants';

export const GOLD_PROGRAM_PAGE =
  'https://sarow.notion.site/Gold-Referral-Program-1c921f62aad74c5f928c0df9d02762b1';

export const GOLD_PROGRAM_TELEGRAM = 'https://t.me/tonplacepartnerbot';

enum InviteTabsEnum {
  LINKS = 'links',
  BOTS = 'bots',
}
enum UsersTabsEnum {
  INVITED = 'invited',
  SUBSCRIBED = 'subscribed',
}

const InviteLinkItem: FC<{
  title?: string;
  description: string;
  link?: string;
  onClick?: () => void;
  userId?: number;
}> = ({ title, description, link, userId, onClick }) => {
  const dispatch = useDispatch();
  const user = useUserInfo(userId || 0);
  const t = useTranslator();

  const handleCopy = useCallback(() => {
    if (link) {
      copyToClipboard(link);
      dispatch(toastActions.setToastSuccess(t('copied')));
    }
  }, [link, dispatch]);

  return (
    <div className="Invite__LinkItem">
      {userId && (
        <div className="Invite__LinkItem__image">
          <UnitPhoto onlineIndicator={false} nftIndicator={false} unit={user} />
        </div>
      )}
      <div className="Invite__LinkItem__content">
        <div className="Invite__LinkItem__title">
          {userId && !title ? <UnitName unit={user} /> : title}
        </div>
        <div className="Invite__LinkItem__description">{description}</div>
      </div>
      <div onClick={onClick || handleCopy} className="Invite__LinkItem__copyLink">
        {onClick ? <IcSocialLink28 /> : <IcCopy20 />}
      </div>
    </div>
  );
};

export const Invite: FC = () => {
  const user = useCurrentUserInfo();
  const dispatch = useDispatch();
  const t = useTranslator();
  const {
    invitedFriends,
    totalInvitedFriends,
    isGold,
    isInvitedLoading,
    invited,
    subscribed,
    profit,
    bots,
    isInvitedFailed,
    referralPercent,
    isStatsLoading,
    bonus,
    pagination,
    bonusFansEnabled,
  } = useSelector(inviteSelector);
  const contentPriority = useSelector(contentPrioritySelector);
  const [typeTab, setTypeTab] = useState(InviteTabsEnum.LINKS);
  const [usersTab, setUsersTab] = useState(UsersTabsEnum.INVITED);
  const { subscriptionRubPrice } = useSelector(mainSelector);
  const isAdult = contentPriority === ContentPriorityType.ADULT;
  const isNewReferralGoldProgram = false;
  //isAdult && (!user || (user.id !== 164669 && user.verified !== 2));

  const { route } = useRoute();
  const from = route.params.from || '';

  const handleCreateLink = () => {
    _prompt(t('invite_link_prompt_text'), (link) => {
      try {
        const url = new URL(link);
        const params = new URLSearchParams(url.search);
        params.append('ref', Number(user.id).toString(16));
        url.search = params.toString();
        copyToClipboard(url.toString());
        dispatch(toastActions.setToastSuccess(t('copied')));
      } catch (e) {
        dispatch(toastActions.setToastFail(t('invite_link_prompt_failed')));
      }
    });
  };

  useEffect(() => {
    if (user.verified === 2) {
      statReachGoal('invite_page');
    }
  }, [user]);

  useEffect(() => {
    isNewReferralGoldProgram
      ? dispatch(inviteActions.loadInviteStats())
      : dispatch(inviteActions.loadInvitesFriends());
  }, [isNewReferralGoldProgram]);

  const hexId = new Number(user.id).toString(16);

  const linkPrepared = useMemo(() => {
    // return `https://${location.host}/${
    //   user.domain ? user.domain : `id${user.id}`
    // }?ref=${hexId}`;

    if (from === 'mine') {
      return `https://${location.host}/mine?ref=${hexId}`;
    } else {
      return `https://${location.host}/u/${user.domain ? user.domain : `id${user.id}`}`;
    }
  }, [user.id, hexId, from]);

  const linkTelegram = useMemo(() => {
    return `https://t.me/${TELEGRAM_BOT_USERNAME}/app?startapp=u${user.id}`;
  }, [user.id, hexId, from]);

  const link = useMemo(() => {
    return encodeURIComponent(linkPrepared);
  }, [linkPrepared]);

  const handleCopy = useCallback(() => {
    copyToClipboard(linkPrepared);
    dispatch(toastActions.setToastSuccess(t('copied')));

    if (user.verified === 2) {
      statReachGoal('invite_link_copied');
    }
  }, [dispatch, linkPrepared, t, user]);

  const handleCopyTelegram = useCallback(() => {
    copyToClipboard(linkTelegram);
    dispatch(toastActions.setToastSuccess(t('copied')));

    if (user.verified === 2) {
      statReachGoal('invite_link_copied');
    }
  }, [dispatch, linkTelegram, t, user]);

  const handleRetry = useCallback(() => {
    dispatch(inviteActions.loadInvitesFriends());
  }, [dispatch]);

  const goTo = useCallback((tab: UsersTabsEnum) => {
    setUsersTab(tab);
    window.location.hash = '';
    setTimeout(() => {
      window.location.hash = tab;
    }, 0);
  }, []);

  const handleEnableGold = useCallback(() => {
    showModal('enable_gold', false);
  }, []);

  const caption = useMemo(() => {
    if (user.id === 164669) {
      return t('invite_caption');
    }

    if (contentPriority === ContentPriorityType.OTHER) {
      return t('invite_caption_media');
    }

    return rnbr(t('invite_caption_adult'));
  }, [t, subscriptionRubPrice, user, contentPriority]);

  function renderFriends() {
    if (isInvitedLoading) {
      return (
        <Placeholder>
          <Loader />
        </Placeholder>
      );
    }

    if (isInvitedFailed) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button onClick={handleRetry} size="small">
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (!invitedFriends.length) {
      return <Placeholder>{t('invite_empty')}</Placeholder>;
    }

    return invitedFriends.map((userId) => (
      <UserCell key={userId} objectId={userId} skipButton />
    ));
  }

  if (
    (isNewReferralGoldProgram && isStatsLoading) ||
    (!isNewReferralGoldProgram && isInvitedLoading)
  ) {
    return <Loader isWrapped />;
  }

  return isNewReferralGoldProgram ? (
    <>
      <MetaTags title={t('invite_title')} />
      <Header showBackButton>{t('invite_title')}</Header>
      <ScrollView
        onLoadMore={() => dispatch(inviteActions.loadMore('stats'))}
        canLoadMore={pagination['stats'].nextFrom !== 0}
      >
        <div className="Invite__listWrapper">
          {/*{isGold ? (*/}
          {/*  <List>*/}
          {/*    <ListItem*/}
          {/*      onClick={() => {*/}
          {/*        window.open(GOLD_PROGRAM_PAGE, '_blank');*/}
          {/*      }}*/}
          {/*      icon={<IcSuccessCheck28 />}*/}
          {/*    >*/}
          {/*      {t('invite_gold_referral_program')}*/}
          {/*    </ListItem>*/}
          {/*  </List>*/}
          {/*) : (*/}
          {/*  <a*/}
          {/*    className="Invite__infoBlock"*/}
          {/*    href={GOLD_PROGRAM_PAGE}*/}
          {/*    target="_blank"*/}
          {/*    rel="noreferrer"*/}
          {/*  >*/}
          {/*    <InfoBlock type={'promo'} title={t('invite_gold_referral_program')} arrow>*/}
          {/*      {t('invite_gold_referral_program_caption')}*/}
          {/*    </InfoBlock>*/}
          {/*  </a>*/}
          {/*)}*/}
          <List>
            <ListItem
              onClick={() => goTo(UsersTabsEnum.INVITED)}
              indicator={pagination['stats'].length.toString()}
            >
              {t('invite_stats_item_referrals')}
            </ListItem>
            <ListItem
              onClick={() => goTo(UsersTabsEnum.SUBSCRIBED)}
              indicator={subscribed.length.toString()}
            >
              {t('invite_stats_item_subscribed')}
            </ListItem>
            <ListItem
              footer={
                !isGold ? (
                  <Button size="medium" onClick={handleEnableGold}>
                    {t('invite_enable_gold_button')}
                  </Button>
                ) : null
              }
              indicator={referralPercent + '%'}
            >
              {t('invite_stats_item_percent')}
            </ListItem>
            <ListItem
              onClick={() => {
                showModal('balance', true, { currency: 'eur' });
              }}
              indicator={
                isGold ? '€' + roundNumber(profit) : roundNumber(profit) + ' TON'
              }
            >
              {t('invite_stats_item_earnings')}
            </ListItem>
            <ListItem chevron href={GOLD_PROGRAM_TELEGRAM}>
              {t('invite_stats_item_telegram')}
            </ListItem>
          </List>
        </div>
        <h3 className="Invite__subtitle">{t('invite_referral_links_title')}</h3>
        <div className="Invite__tabsWrapper">
          <Tabs
            selected={typeTab}
            onChange={(value) => setTypeTab(value as InviteTabsEnum)}
          >
            <TabsItem value={InviteTabsEnum.LINKS}>{t('invite_tab_links')}</TabsItem>
            <TabsItem value={InviteTabsEnum.BOTS}>{t('invite_tab_bots')}</TabsItem>
          </Tabs>
        </div>
        {typeTab === InviteTabsEnum.LINKS ? (
          <div className="Invite__linksWrapper">
            <div className="Invite__linksWrapper__description">
              {t('invite_program_description')}
            </div>
            <div className="Invite__linksWrapper__description">
              {t('invite_program_percent_description', {
                percent: referralPercent,
              })}
            </div>
            <InviteLinkItem
              title={t('invite_link_landing_title')}
              description={t('invite_link_landing_description')}
              link={'https://ton.place?ref=' + hexId + '&c=erotic'}
            />
            <InviteLinkItem
              title={t('invite_link_paywall_title')}
              description={t('invite_link_paywall_description')}
              link={'https://ton.place/subscription/adult?ref=' + hexId}
            />
            <InviteLinkItem
              title={t('invite_link_generate_title')}
              description={t('invite_link_generate_description')}
              onClick={handleCreateLink}
            />

            <div className="Invite__linksWrapper__description">
              {t('invite_terms_pref')}{' '}
              <a
                href={getDocumentHref('terms', 'referrals')}
                target="_blank"
                rel="noreferrer"
              >
                {t('invite_terms')}
              </a>
            </div>
          </div>
        ) : (
          <div className="Invite__linksWrapper">
            <div className="Invite__linksWrapper__description">
              {t('invite_program_percent_description', {
                percent: referralPercent,
              })}
            </div>
            {bots.map((bot) => (
              <InviteLinkItem
                key={bot.userId}
                userId={bot.userId}
                title={'@' + bot.name}
                description={t('invite_bot_subscription_price', {
                  amount: bot.subscriptionPrice,
                })}
                link={'https://t.me/' + bot.name + '?start=r-' + hexId}
              />
            ))}
          </div>
        )}
        <h3 className="Invite__subtitle">{t('invite_referrals_title')}</h3>
        <div className="Invite__tabsWrapper">
          <Tabs
            selected={usersTab}
            onChange={(value) => setUsersTab(value as UsersTabsEnum)}
          >
            <TabsItem counter={pagination['stats'].length} value={UsersTabsEnum.INVITED}>
              {t('invite_referrals_tab_invited')}
            </TabsItem>
            <TabsItem counter={subscribed.length} value={UsersTabsEnum.SUBSCRIBED}>
              {t('invite_referrals_tab_subscribed')}
            </TabsItem>
          </Tabs>
        </div>
        <div className="Invite__userList" id={usersTab}>
          {((!subscribed.length && usersTab === UsersTabsEnum.SUBSCRIBED) ||
            (!invited.length && usersTab === UsersTabsEnum.INVITED)) && (
            <Placeholder title={t('invite_referral_empty_title')}>
              {t('invite_referral_empty_description')}
            </Placeholder>
          )}
          {usersTab === UsersTabsEnum.SUBSCRIBED
            ? subscribed.map((userId) => (
                <UserCell key={userId} objectId={userId} skipButton />
              ))
            : invited.map((userId) => (
                <UserCell key={userId} objectId={userId} skipButton />
              ))}
        </div>
        {pagination['stats'].isLoading && <Loader mode="additional" />}
      </ScrollView>
    </>
  ) : (
    <>
      <MetaTags title={t('invite_title')} />
      <Header showBackButton>{t('invite_title')}</Header>
      <ScrollView
        onLoadMore={() => dispatch(inviteActions.loadMore('friends'))}
        canLoadMore={pagination['friends'].nextFrom !== 0}
      >
        <PromoLinks
          referralLink={linkPrepared}
          telegramLink={user.verified === 2 ? linkTelegram : undefined}
          onCopyReferral={handleCopy}
          onCopyTelegram={handleCopyTelegram}
        />
        {(bonusFansEnabled || user.id === 2407817) && (
          <InviteBonusFans inviteLink={linkPrepared} />
        )}
        {!!bonus && <InviteBonus bonus={bonus} />}
        <div className="Invite__link_wrap">
          {user.verified === 2 && (
            <div className="Invite__link_title">{t('invite_title_adult')}</div>
          )}
          <div className="Invite__link_description">{caption}</div>
          {/*<div className="Invite__link_label">{t('invite_link')}</div>*/}
          {/*<div className="Invite__link" onClick={handleCopy}>*/}
          {/*  {linkPrepared}*/}
          {/*</div>*/}
          {/*<Button onClick={handleCopy} size="small" stretched icon={<IcCopy20 />}>*/}
          {/*  {t('invite_link_copy')}*/}
          {/*</Button>*/}
          {/*<div className="Invite__link__tip">*/}
          {/*  {t('invite_terms_pref')}{' '}*/}
          {/*  <a*/}
          {/*    href={getDocumentHref('terms', 'referrals')}*/}
          {/*    target="_blank"*/}
          {/*    rel="noreferrer"*/}
          {/*  >*/}
          {/*    {t('invite_terms')}*/}
          {/*  </a>*/}
          {/*  .*/}
          {/*</div>*/}
        </div>
        <div className="Invite__buttons_wrap">
          <InlineHeader>{t('invite_share')}</InlineHeader>
          <SocialButtons link={link} />
        </div>
        <InlineHeader>
          {t('invite_friends')}{' '}
          {totalInvitedFriends > 0 ? `(${pagination['friends'].length})` : ''}
        </InlineHeader>
        <div className="Invite__friends__tip">{t('invite_friends_tip')}</div>
        {renderFriends()}
        {pagination['friends'].isLoading && <Loader mode="additional" />}
      </ScrollView>
    </>
  );
};
